import type { Attachment, Primitive } from '@sentry/types';
import * as Sentry from '@sentry/capacitor';
import { atom, useRecoilValue } from 'recoil';
import { DebugContext } from './types';
import {
  getMapDebugSentryAttachments,
  getMapDebugSentryTags,
} from './mapDebugData';

type UseCaptureDebugDataReturn = {
  debugContext: DebugContext;
  captureDebugData: () => Promise<void>;
};

const debugContextAtom = atom<DebugContext>({
  key: 'debugContext',
  default: {
    map: undefined,
  },
  // This atom is used to store references to objects that are not serializable
  // for the purpose of sending information about them to Sentry. While dangerous
  // to allow mutability in general, this is the only way to store references
  // from lower in the JSX tree to where `captureDebugData()` is called.
  dangerouslyAllowMutability: true,
});

const captureDebugDataAndSendToSentry = async (
  debugContext: DebugContext,
): Promise<void> => {
  const { map } = debugContext;
  let attachments: Array<Attachment> = [];
  let tags: Record<string, Primitive> = {};

  if (map) {
    attachments = attachments.concat(await getMapDebugSentryAttachments(map));
    tags = {
      ...tags,
      ...getMapDebugSentryTags(map),
    };
  }

  attachments.forEach((attachment) => {
    Sentry.getCurrentScope().addAttachment(attachment);
  });
  Sentry.captureMessage('debug-data-captured', {
    tags,
  });
  Sentry.getCurrentScope().clearAttachments();
  await Sentry.getCurrentScope().getClient()?.flush(10000);
};

export const useCaptureDebugData = (): UseCaptureDebugDataReturn => {
  const debugContext = useRecoilValue(debugContextAtom);

  const captureDebugData = (): Promise<void> => {
    return captureDebugDataAndSendToSentry(debugContext);
  };

  return { debugContext, captureDebugData };
};
